import { useState, useEffect } from "react";
import { t, font } from "@realtyflows/ui";
import { getClientScoreHistory } from "../api";

/**
 * ScoreHistoryChart — V1 mini line chart of a client's AI score over the last 30 entries.
 *
 * Renders as an inline SVG sparkline so we don't need a chart library.
 * Hides itself when there are fewer than 2 data points (nothing to plot yet).
 *
 * Props:
 *   clientId  — id of the client whose history to fetch
 *   height    — pixel height of the chart (default 80)
 */
export default function ScoreHistoryChart({ clientId, height = 80 }) {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!clientId) return;
    getClientScoreHistory(clientId)
      .then((rows) => setHistory(rows || []))
      .catch(() => setHistory([]))
      .finally(() => setLoading(false));
  }, [clientId]);

  if (loading) return null;
  if (history.length < 2) return null; // Nothing meaningful to chart

  // Sort oldest -> newest so the line goes left -> right
  const points = [...history].reverse().map((row) => ({
    score: row.score,
    at: new Date(row.recorded_at),
  }));

  const width = 320;          // Logical SVG width — scales via viewBox
  const padX = 8;
  const padY = 6;
  const w = width - padX * 2;
  const h = height - padY * 2;

  const scores = points.map((p) => p.score);
  const minScore = Math.min(...scores, 0);
  const maxScore = Math.max(...scores, 100);
  const range = Math.max(1, maxScore - minScore);

  const xStep = points.length > 1 ? w / (points.length - 1) : 0;
  const coords = points.map((p, i) => ({
    x: padX + i * xStep,
    y: padY + h - ((p.score - minScore) / range) * h,
    score: p.score,
  }));

  const pathD = coords.map((c, i) => `${i === 0 ? "M" : "L"} ${c.x.toFixed(1)} ${c.y.toFixed(1)}`).join(" ");
  const areaD = `${pathD} L ${coords[coords.length - 1].x} ${padY + h} L ${coords[0].x} ${padY + h} Z`;

  const latest = coords[coords.length - 1];
  const first = coords[0];
  const delta = latest.score - first.score;
  const deltaColor = delta > 0 ? t.emerald : delta < 0 ? t.coral : t.textMuted;
  const lineColor = latest.score >= 80 ? t.emerald : latest.score >= 50 ? t.amber : t.coral;

  const fmtDate = (d) => d.toLocaleDateString(undefined, { month: "short", day: "numeric" });

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
        <span style={{ fontSize: 12, color: t.textMuted, fontFamily: font.body }}>
          {points.length}-point history
        </span>
        <span style={{ fontSize: 11, color: deltaColor, fontFamily: font.mono, fontWeight: 700 }}>
          {delta > 0 ? "+" : ""}{delta} pts
        </span>
      </div>

      <svg
        viewBox={`0 0 ${width} ${height}`}
        preserveAspectRatio="none"
        style={{ width: "100%", height, display: "block" }}
      >
        <defs>
          <linearGradient id={`scoreGradient-${clientId}`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%"   stopColor={lineColor} stopOpacity="0.32" />
            <stop offset="100%" stopColor={lineColor} stopOpacity="0" />
          </linearGradient>
        </defs>
        <path d={areaD} fill={`url(#scoreGradient-${clientId})`} />
        <path d={pathD} fill="none" stroke={lineColor} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
        {/* Endpoint dot */}
        <circle cx={latest.x} cy={latest.y} r="3.2" fill={lineColor} stroke={t.surface} strokeWidth="1.5" />
      </svg>

      <div style={{ display: "flex", justifyContent: "space-between", marginTop: 6 }}>
        <span style={{ fontSize: 10, color: t.textMuted, fontFamily: font.mono }}>{fmtDate(points[0].at)}</span>
        <span style={{ fontSize: 10, color: t.textMuted, fontFamily: font.mono }}>{fmtDate(points[points.length - 1].at)}</span>
      </div>
    </div>
  );
}
